<!-- =========================================================================================
    File Name: Main.vue
    Description: Main layout
    ----------------------------------------------------------------------------------------
    Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
    Author: Pixinvent
    Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
  <div
    class="layout--main"
    :class="[layoutTypeClass, navbarClasses, footerClasses, { 'no-scroll': isAppPage }]"
  >
    <v-nav-menu :nav-menu-items="navMenuItems" parent=".layout--main" />

    <div id="content-area" :class="[contentAreaClass, { 'show-overlay': bodyOverlay }]">
      <div id="content-overlay" />

      <template>
        <the-navbar-vertical
          :navbar-color="navbarColor"
          :class="[
            { 'text-white': isNavbarDark && !isThemeDark },
            { 'text-base': !isNavbarDark && isThemeDark },
          ]"
        />
      </template>
      <!-- /Navbar -->

      <div class="content-wrapper">
        <div class="router-view">
          <!-- Override the 'margin-top' for the home page because we don't want to have a space
          between the sticky navbar and our background with our logo -->
          <div
            class="router-content"
            :style="[
              isHomePage && navbarType == 'sticky' ? { 'margin-top': '1.0rem !important' } : {},
            ]"
          >
            <div class="content-area__content">
              <back-to-top
                v-if="!hideScrollToTop"
                bottom="5%"
                :right="$vs.rtl ? 'calc(100% - 2.2rem - 38px)' : '30px'"
                visibleoffset="500"
              >
                <vs-button
                  icon-pack="feather"
                  icon="icon-arrow-up"
                  class="shadow-lg btn-back-to-top"
                />
              </back-to-top>

              <!--
              NOTE: Removed for now because making our life complicated with graphs and analytics
              (see Asana task).
              <transition :name="routerTransition" mode="out-in">
              -->
              <router-view
                :key="$route.fullPath"
                @changeRouteTitle="changeRouteTitle"
                @setAppClasses="(classesStr) => $emit('setAppClasses', classesStr)"
              />
              <!--
              </transition>
              -->
            </div>
          </div>
        </div>
      </div>
      <the-footer />
      <cookies />
    </div>
  </div>
</template>

<script>
import BackToTop from "vue-backtotop";

import themeConfig from "@/../themeConfig.js";
import { hasAnyRole, hasAnyRoles } from "@/acl";
import { Role } from "@/acl/roles";
import { SERVER_ERROR_MESSAGE } from "@/assets/constants/global";
import Cookies from "@/components/compliance/Cookies.vue";
import config from "@/config/alquant";
import TheNavbarVertical from "@/layouts/components/navbar/TheNavbarVertical.vue";
import TheFooter from "@/layouts/components/TheFooter.vue";
import VNavMenu from "@/layouts/components/vertical-nav-menu/VerticalNavMenu.vue";

export default {
  components: {
    BackToTop,
    TheFooter,
    TheNavbarVertical,
    VNavMenu,
    Cookies,
  },
  data() {
    return {
      serverErrorMessage: SERVER_ERROR_MESSAGE,
      footerType: themeConfig.footerType || "static",
      hideScrollToTop: themeConfig.hideScrollToTop,
      isNavbarDark: false,
      navbarColor: themeConfig.navbarColor || "#fff",
      navbarType: themeConfig.navbarType || "floating",
      routerTransition: themeConfig.routerTransition || "none",
      routeTitle: this.$route.meta.pageTitle,
    };
  },
  computed: {
    isUserAuthenticated() {
      return hasAnyRole(Role.insight);
    },
    hasIndicatorRole() {
      return hasAnyRoles([Role.admin, Role.superAdmin, Role.employee, Role.indicator]);
    },
    filteredIndicators() {
      if (config.indicators) {
        return config.indicators.filter(
          (indicatorDetail) =>
            indicatorDetail.allowedRoles == undefined || hasAnyRoles(indicatorDetail.allowedRoles)
        );
      }
      return [];
    },
    allCustomIndicators() {
      return this.$store.getters["customIndicators/getCustomIndicators"];
    },
    allPortfolios() {
      return this.$store.getters["portfolios/getPortfolios"];
    },
    navMenuItems() {
      const menu = [];

      // TODO: put it back when we have a way to make it fast enough without taking
      // all the resources of the backend.
      /*
      // Dashboard (only if indicator role)
      if (this.hasIndicatorRole) {
        menu.push({
          url: "/dashboard",
          name: "Dashboard",
          slug: "dashboard",
          icon: "TrelloIcon",
        });
      }
      */

      // Indicators
      const alquantIndicatorsSubmenu = [];
      // Add all Alquant indicators.
      alquantIndicatorsSubmenu.push(
        ...this.filteredIndicators.map((indicatorDetail) => ({
          name: indicatorDetail.name,
          slug: `indicators-${indicatorDetail.id}`,
          icon: indicatorDetail.icon,
          submenu: [
            {
              url: `/indicators/${indicatorDetail.id}`,
              name: "Overview",
              slug: `${indicatorDetail.id}-overview`,
              icon: "EyeIcon",
            },
            {
              url: `/indicators/${indicatorDetail.id}/use-case`,
              name: "Use Case",
              slug: `${indicatorDetail.id}-use-case`,
              icon: "BarChart2Icon",
            },
            {
              url: `/indicators/${indicatorDetail.id}/notifications`,
              name: "Notifications",
              slug: `${indicatorDetail.id}-notifications`,
              icon: "BellIcon",
            },
            {
              url: `/indicators/${indicatorDetail.id}/documents`,
              name: "Documents",
              slug: `${indicatorDetail.id}-documents`,
              icon: "DownloadIcon",
            },
          ],
        }))
      );

      menu.push({
        name: "Alquant Indicators",
        slug: "alquant-indicators",
        submenu: alquantIndicatorsSubmenu,
      });

      // Add all custom indicators (only if user has the rights).
      const customIndicatorsSubmenu = [];

      // If there is an issue with firestore, warn the user.
      if (this.allCustomIndicators === null && this.allPortfolios !== null) {
        this.$vs.dialog({
          type: "alert",
          color: "danger",
          title: "Server error",
          text: 'An error occured in our server, hence the "CUSTOM INDICATORS" and "TOOLS" sections are unavailable at the moment. A report has been sent to our team and we will fix it as soon as possible. Don\'t hesitate to contact us if the issue persists.',
        });
      } else if (this.allCustomIndicators != null && this.allCustomIndicators.length > 0) {
        customIndicatorsSubmenu.push(
          ...this.allCustomIndicators.map((customIndicator) => {
            const menuItem = {
              name: customIndicator.shortName || customIndicator.name,
              slug: `custom-indicator-${customIndicator.id}`,
              submenu: [
                {
                  url: `/custom-indicators/${customIndicator.id}`,
                  name: "Use Case",
                  slug: `custom-indicator-${customIndicator.id}-use-case`,
                  icon: "BarChart2Icon",
                },
                {
                  url: `/custom-indicators/${customIndicator.id}/notifications`,
                  name: "Notifications",
                  slug: `custom-indicator-${customIndicator.id}-notifications`,
                  icon: "BellIcon",
                },
                {
                  url: `/custom-indicators/${customIndicator.id}/edit`,
                  name: "Edit",
                  slug: `custom-indicator-${customIndicator.id}-edit`,
                  icon: "Edit2Icon",
                },
              ],
            };
            if (customIndicator.type && customIndicator.type === "predefined") {
              menuItem["tag"] = "Alquant";
              menuItem["tagColor"] = "primary";
              menuItem["tagTooltip"] = "This is a custom indicator created by Alquant.";
            }

            return menuItem;
          })
        );
      }

      // If firestore doesn't respond us with the custom indicator, we will show an error
      // by dialog stating that custom indicator service isn't available.
      if (this.allCustomIndicators !== null) {
        customIndicatorsSubmenu.push({
          url: "/custom-indicators/new",
          name: "Create new indicator",
          slug: "new-custom-indicator",
          icon: "PlusIcon",
          featured: true,
        });

        menu.push({
          name: "Custom Indicators",
          slug: "custom-indicators",
          submenu: customIndicatorsSubmenu,
        });
      }

      // Add all portfolios.
      const portfoliosSubmenu = [];

      // If there is an issue with firestore, warn the user.
      if (this.allPortfolios === null && this.allCustomIndicators !== null) {
        this.$vs.dialog({
          type: "alert",
          color: "danger",
          title: "Server error",
          text: 'An error occured in our server, hence the "PORTFOLIOS" and "TOOLS" sections are unavailable at the moment. A report has been sent to our team and we will fix it as soon as possible. Don\'t hesitate to contact us if the issue persists.',
        });
      } else if (this.allPortfolios != null && this.allPortfolios.length > 0) {
        portfoliosSubmenu.push(
          ...this.allPortfolios.map((portfolio) => {
            const menuItem = {
              name: portfolio.name,
              slug: `portfolio-${portfolio.id}`,
              submenu: [
                {
                  url: `/portfolios/${portfolio.id}`,
                  name: "Simulation",
                  slug: `customIndicator-${portfolio.id}-simulation`,
                  icon: "BarChart2Icon",
                },
                {
                  url: `/portfolios/${portfolio.id}/edit`,
                  name: "Edit",
                  slug: `portfolio-${portfolio.id}-edit`,
                  icon: "Edit2Icon",
                },
              ],
            };
            if (portfolio.type && portfolio.type === "predefined") {
              menuItem["tag"] = "Alquant";
              menuItem["tagColor"] = "primary";
              menuItem["tagTooltip"] = "This is a portfolio created by Alquant.";
            }

            return menuItem;
          })
        );
      }

      // If firestore doesn't respond us with the custom indicator, we will show an error
      // by dialog stating that portfolios service isn't available.
      if (this.allPortfolios !== null) {
        portfoliosSubmenu.push({
          url: "/portfolios/new",
          name: "Create new portfolio",
          slug: "new-portfolio",
          icon: "PlusIcon",
          featured: true,
        });

        menu.push({
          name: "Portfolios",
          slug: "portfolios",
          submenu: portfoliosSubmenu,
        });
      }

      if (this.allCustomIndicators === null && this.allPortfolios === null) {
        this.$vs.dialog({
          type: "alert",
          color: "danger",
          title: "Server error",
          text: 'An error occured in our server, hence the "CUSTOM INDICATORS", "PORTFOLIOS" and "TOOLS" sections are unavailable at the moment. A report has been sent to our team and we will fix it as soon as possible. Don\'t hesitate to contact us if the issue persists.',
        });
      }

      // Add tools.
      // If firestore doesn't respond us with the custom indicator, we will show an error
      // by dialog stating that portfolios service isn't available.
      if (this.allCustomIndicators !== null && this.allPortfolios !== null) {
        const toolsSubmenu = [
          {
            url: "/tools/tactical",
            name: "Tactical Asset Allocation",
            slug: "tools-tactical",
            icon: "GridIcon",
          },
        ];

        menu.push({ name: "Tools", slug: "tools", submenu: toolsSubmenu });
      }

      // Help
      const helpSubmenu = [];

      // Knowledge Base
      helpSubmenu.push({
        url: "/help/knowledge-base",
        name: "Knowledge Base",
        slug: "knowledge-base",
        icon: "InfoIcon",
      });

      // Videos
      helpSubmenu.push({
        url: "/help/videos",
        name: "Videos",
        slug: "videos",
        icon: "YoutubeIcon",
      });

      // FAQ
      helpSubmenu.push({
        url: "/help/faq",
        name: "FAQ",
        slug: "faq",
        icon: "HelpCircleIcon",
      });

      // Contact
      helpSubmenu.push({
        url: "/help/contact",
        name: "Contact",
        slug: "contact",
        icon: "MailIcon",
      });

      menu.push({ name: "Help", slug: "help", submenu: helpSubmenu });

      // Admin
      menu.push({
        name: "Admin",
        meta: {
          authorize: [Role.admin, Role.superAdmin],
        },
        submenu: [
          {
            url: "/admin/users",
            name: "List of users",
            slug: "users-list",
            icon: "UsersIcon",
          },
        ],
      });

      return menu;
    },
    bodyOverlay() {
      return this.$store.state.bodyOverlay;
    },
    contentAreaClass() {
      if (this.mainLayoutType === "vertical") {
        if (this.verticalNavMenuWidth == "default") return "content-area-reduced";
        else if (this.verticalNavMenuWidth == "reduced") return "content-area-lg";
        else return "content-area-full";
      }
      // else if(this.mainLayoutType === "boxed") return "content-area-reduced"
      else return "content-area-full";
    },
    footerClasses() {
      return {
        "footer-hidden": this.footerType == "hidden",
        "footer-sticky": this.footerType == "sticky",
        "footer-static": this.footerType == "static",
      };
    },
    isAppPage() {
      return this.$route.meta.no_scroll;
    },
    isThemeDark() {
      return this.$store.state.theme == "dark";
    },
    layoutTypeClass() {
      return `main-${this.mainLayoutType}`;
    },
    mainLayoutType() {
      return this.$store.state.mainLayoutType;
    },
    navbarClasses() {
      return {
        "navbar-hidden": this.navbarType == "hidden",
        "navbar-sticky": this.navbarType == "sticky",
        "navbar-static": this.navbarType == "static",
        "navbar-floating": this.navbarType == "floating",
      };
    },
    verticalNavMenuWidth() {
      return this.$store.state.verticalNavMenuWidth;
    },
    windowWidth() {
      return this.$store.state.windowWidth;
    },
    isHomePage() {
      return this.$route.path == "/" || this.$route.path == "/new";
    },
  },
  watch: {
    $route() {
      this.routeTitle = this.$route.meta.pageTitle;
    },
    isThemeDark(val) {
      const color = this.navbarColor == "#fff" && val ? "#10163a" : "#fff";
      this.updateNavbarColor(color);
    },
    "$store.state.mainLayoutType"(val) {
      this.setNavMenuVisibility(val);
    },
  },
  created() {
    const color = this.navbarColor == "#fff" && this.isThemeDark ? "#10163a" : this.navbarColor;
    this.updateNavbarColor(color);
    this.setNavMenuVisibility(this.$store.state.mainLayoutType);
  },
  methods: {
    changeRouteTitle(title) {
      this.routeTitle = title;
    },
    updateNavbarColor(val) {
      this.navbarColor = val;
      if (val == "#fff") this.isNavbarDark = false;
      else this.isNavbarDark = true;
    },
    setNavMenuVisibility(layoutType) {
      if (
        (layoutType === "horizontal" && this.windowWidth >= 1200) ||
        (layoutType === "vertical" && this.windowWidth < 1200)
      ) {
        this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", false);
        this.$store.dispatch("updateVerticalNavMenuWidth", "no-nav-menu");
      } else {
        this.$store.commit("TOGGLE_IS_VERTICAL_NAV_MENU_ACTIVE", true);
      }
    },
  },
};
</script>
